import type { IBatchStore } from './batch-store-types';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import type { IScale } from '../../../types/device/DeviceModels';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import convertUnit from '../../utils/unit-converter';
import { addFeedLog } from '../../containers/Batch/DataToBatch';
import { ToleranceMode } from '../../constants/Configuration';
import {
  BatchAction,
  JogType,
  WeighedMaterialType,
} from '../../constants/MaterialModels';
import { reaction } from 'mobx';
import { waterShouldWaitForMoistureProbe } from './live-moisture';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { FeedOperation } from '../../constants/FeedOperation';
import { checkIfDischargeStartPercentForPumpsReady } from './checkAdmixtureDischargeConditions';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { beltSwitch } from './device-switches/belt-switch';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import { isRunModeNewYork } from './run-mode';

const FEATURE_LIVE_MOISTURE_FOR_WEIGHED = isFeatureEnabled('moisture_for_weighed');

export const jogModeMixin = {

  getScaleWeightAfterSettlingTime(this: IBatchStore, scale: IScale, settlingTime = 2, onFinish: (weight: number) => void = () => {/* empty */}) {

    this.timers.set(timerKey.scaleWeight(scale.id), () => {
      let weight = this.getScaleWeight(scale);
      logger.log(`scale #${scale.id} weight:`, weight, 'settlingTime:', settlingTime);

      onFinish(weight);
    }, settlingTime * 1000, `Get scale weight after settling - scale #${scale.id}`);
  },

  updateInventory(this: IBatchStore, data: IWeighedDataToBatch, weight: number, initialData: IBatchData, manuallyTerminated = false) {

    const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const { material, materialDetails, scale, mft } = data;

    const runningDataForScale = runningData ? runningData.scales[scale.id] : null;
    if (!runningData || !runningDataForScale) {
      logger.error('no running data for scale', scale.id);
      return;
    }
    // Get the current scale data which may have been updated during substitution
    const scaleIndex = runningData ? runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId) : -1;
    const scaleData = scaleIndex >= 0 ? runningData.scales[scale.id][scaleIndex] : null;

    // If we have scaleData with substitution info, use that instead of the passed data
    const dataForBins = scaleData?.isSubstituted ? scaleData : data;
    const bins = this.getBins(dataForBins);
    const primaryBin = bins.primaryBin || {};
    const secondaryBin = bins.secondaryBin;

    // We already got scaleData above, just get the deduction
    const deduction = scaleData ? scaleData.targetDeduction : 0;

    // Debug bin selection
    logger.log('[UPDATE_INVENTORY] Bin selection debug', {
      material: material.code,
      dataBinId: data.bin ? data.bin.id : 'none',
      primaryBinId: primaryBin.id,
      isSubstituted: data.isSubstituted,
      hasBinPercent: !!data.binPercent,
      binPercentValue: data.bin && data.binPercent ? data.binPercent[data.bin.id] : undefined,
    });

    const b = this.getDeviceStatus(primaryBin.id);

    // Log the device status we're using for weight calculation
    logger.log('[UPDATE_INVENTORY] Device status for weight calculation', {
      material: material.code,
      binId: primaryBin.id,
      prevWeight: b.prevWeight,
      weight: b.weight,
      target: b.target,
      status: b.status,
      fullStatus: b,
    });

    // Additional logging to debug substitute material weight issues
    if (scaleData?.isSubstituted) {
      logger.log('[UPDATE_INVENTORY] Substitute material bin details', {
        material: material.code,
        scaleDataBinId: scaleData.bin ? scaleData.bin.id : 'none',
        originalBinId: scaleData.originalBin ? scaleData.originalBin.id : 'none',
        substitutionWeight: scaleData.substitutionWeight,
        startWeight: scaleData.startWeight,
      });

      // Also log the device status for the substitute bin
      if (scaleData.bin) {
        const substituteBinStatus = this.getDeviceStatus(scaleData.bin.id);
        logger.log('[UPDATE_INVENTORY] Substitute bin device status', {
          binId: scaleData.bin.id,
          prevWeight: substituteBinStatus.prevWeight,
          weight: substituteBinStatus.weight,
          target: substituteBinStatus.target,
          status: substituteBinStatus.status,
          fullStatus: substituteBinStatus,
        });
      }

      // Check if the bin we're using matches the substitute bin
      if (scaleData.bin && primaryBin.id !== scaleData.bin.id) {
        logger.warn('[UPDATE_INVENTORY] BIN MISMATCH!', {
          primaryBinId: primaryBin.id,
          substituteBinId: scaleData.bin.id,
        });
      }
    }

    if (scale.allowOvercapacityInOneDrop && data.overcapacityTarget && b.prevWeight < 0) {
      b.prevWeight = 0;
    }
    let actualWeight = deduction <= 0 ? weight - b.prevWeight - deduction : weight - b.prevWeight + deduction;
    let targetPerDrop = isRunModeNewYork(initialData.runMode) || this.settingsStore.configuration?.toleranceMode === ToleranceMode.NYDOTCumulative
      ? mft
      : b.target;
    let actualPerDrop = actualWeight;

    if (scaleData.proportionOneTarget && scaleData.proportionTwoTarget) {
      targetPerDrop = scaleData.proportionOneTarget + scaleData.proportionTwoTarget;
      if (scaleData.proportionOneActual) {
        actualPerDrop = scaleData.proportionOneActual + actualWeight;
      } else {
        scaleData.proportionOneActual = actualWeight;
        actualPerDrop = scaleData.proportionOneActual;
      }
    }

    const scaleUnit = this.getUnitById(scale.unitId);

    logger.log('[UPDATE_INVENTORY] Weight calculation details', {
      material: material.code,
      scaleWeight: weight,
      prevWeight: b.prevWeight,
      deduction,
      calculatedActualWeight: actualWeight,
      primaryBinId: primaryBin.id,
      binFromData: data.bin ? data.bin.id : 'none',
      isSubstitute: scaleData ? scaleData.isSubstituted : false,
    });

    if (this.checkIndicatorValueError(weight) === DeviceStatus.INDICATOR_ERROR) {
      logger.log('indicator error', scale.id, scale.description);
      return;
    }

    if (runningData.mixDesign) {
      logger.log('[UPDATE_INVENTORY] Starting mixDesign update', {
        material: material.code,
        materialId: material.itemId,
        actualWeight,
        manuallyTerminated,
      });

      runningData.mixDesign.forEach((v: MixDesignItem, i: number) => {
        // Check if this is the material being updated (either original or substitute)
        const isMatchingMaterial = (v.batchCode && v.batchCode === material.batchCode) ||
                                  v.constituentItemId === material.itemId;

        // Also check if this is a substitute material that was swapped in
        const isSubstituteForThis = v.isSubstitute && v.originalMaterialId === material.itemId;

        if (isMatchingMaterial || isSubstituteForThis) {
          logger.log('[UPDATE_INVENTORY] Found matching material in mixDesign', {
            material: v.constituentItemCode,
            isMatchingMaterial,
            isSubstituteForThis,
            wasSubstituted: v.wasSubstituted,
            isSubstitute: v.isSubstitute,
            currentActualWeight: v.actualWeight || 0,
          });

          if (v.unitSymbol !== scaleUnit) {
            runningData.mixDesign[i].scaleUnit = scaleUnit;
            runningData.mixDesign[i].scaleWeight = actualWeight;
            actualWeight = toNumber(convertUnit(actualWeight, scaleUnit, v.unitSymbol), 2);
            targetPerDrop = toNumber(convertUnit(targetPerDrop, scaleUnit, v.unitSymbol), 2);
            actualPerDrop = toNumber(convertUnit(actualPerDrop, scaleUnit, v.unitSymbol), 2);
          }

          // Handle actualWeight based on substitution status
          if ((!v.wasSubstituted && !v.isSubstitute) || scaleData?.extraWater) {
            // Normal material - accumulate weight
            if (!runningData.mixDesign[i].actualWeight) {
              runningData.mixDesign[i].actualWeight = 0;
            }
            const previousWeight = runningData.mixDesign[i].actualWeight;
            runningData.mixDesign[i].actualWeight += actualWeight;

            logger.log('[UPDATE_INVENTORY] Updated normal material weight', {
              material: v.constituentItemCode,
              previousWeight,
              addedWeight: actualWeight,
              newTotalWeight: runningData.mixDesign[i].actualWeight,
            });
          } else if (v.isSubstitute) {
            // Substitute material - update with actual dispensed amount
            // The actualWeight here is already calculated as (scaleWeight - prevWeight)
            // where prevWeight was set to the scale weight at time of substitution
            const previousEstimate = runningData.mixDesign[i].actualWeight || 0;
            runningData.mixDesign[i].actualWeight = actualWeight;

            logger.log('[UPDATE_INVENTORY] Updated substitute material with actual dispensed weight', {
              material: v.constituentItemCode,
              previousEstimate,
              actualDispensed: actualWeight,
              difference: actualWeight - previousEstimate,
              originalMaterial: v.originalMaterialCode,
            });
          } else if (v.wasSubstituted) {
            // Original material that was substituted - weight already set during substitution
            logger.log('[UPDATE_INVENTORY] Skipping weight update for substituted material', {
              material: v.constituentItemCode,
              currentWeight: v.actualWeight,
              substitutedWith: v.substituteMaterialCode,
            });
          }

          if (scaleData?.extraWater) {
            runningData.mixDesign[i].actualPerDrop += actualPerDrop;
            runningData.mixDesign[i].targetPerDrop += targetPerDrop;
          } else {
            runningData.mixDesign[i].actualPerDrop = actualPerDrop;
            runningData.mixDesign[i].targetPerDrop = targetPerDrop;
          }
          runningData.mixDesign[i].manuallyTerminated = manuallyTerminated;

          if (materialDetails.liveMoisture) {
            runningData.mixDesign[i].moisture = materialDetails.autoMoistureAmount;
            logger.log('live moisture, updating moisture for ticket:', v.constituentItemCode, materialDetails.autoMoistureAmount);
          }

          logger.log('[UPDATE_INVENTORY] Final weight for material', {
            material: v.constituentItemCode,
            finalActualWeight: runningData.mixDesign[i].actualWeight,
            target: v.quantityFinal || v.quantity,
            targetPerDrop,
            actualPerDrop,
          });
          this.saveResultForDrop(runningData, runningData.mixDesign[i]);
        }
      });
    } else if (this.canDemoBatch || !this.isDemo) {
      const payload = {
        quantity: actualWeight,
        deviceId: scale.id,
        deviceDescription: scale.description,
        itemId: material.itemId,
        itemCode: material.code,
        unitId: scale.unitId,
        unitSymbol: this.getUnitById(scale.unitId),
      };
      logger.log('simple ticket:', payload);
      this.ticketStore.createSimpleTicket(payload);
    }

    const inventoryWeight = deduction <= 0 ? actualWeight - b.weightUpdated + deduction : actualWeight - b.weightUpdated - deduction;
    const quantity = toNumber(convertUnit(inventoryWeight, this.getUnitById(scale.unitId), material.itemInventoryUnitSymbol), 4);
    logger.log('update material inventory', material.code, quantity, material.itemInventoryUnitSymbol, inventoryWeight,
      'manuallyTerminated:', manuallyTerminated, 'bins:', primaryBin.id, secondaryBin?.id);
    if (this.canReduceInventory && quantity > 0) {
      this.materialStore.updateMaterialInventoryUse(material, {
        quantity,
        comment: '',
        ticket: initialData.tId,
        isAutomatic: !initialData.materialId,
        binIds: [ primaryBin?.id, secondaryBin?.id ].filter(Boolean),
      });
    }
  },

  updateInventoryOnPause(this: IBatchStore, deviceId: number) {

    const bin = this.devicesStore.getDeviceById(Number(deviceId));
    if (!bin) {
      return;
    }

    const scale = this.devicesStore.getDeviceById(bin.scaleId);
    if (!scale) {
      return;
    }

    const material = this.materialStore.getMaterialForBin(bin.id);
    if (!material) {
      return;
    }

    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const b = this.getDeviceStatus(bin.id);
    const initialData = this.running.find(v => v.uid === b.uid && v.currentDrop === b.currentDrop);

    if (!initialData || !materialDetails || b.status === DeviceStatus.OVER) {
      return;
    }

    this.setTimeout(() => {
      const currentWeight = this.getScaleWeight(scale);
      const weight = currentWeight - b.prevWeight - b.weightUpdated;
      const scaleUnit = this.getUnitById(scale.unitId);
      const quantity = toNumber(convertUnit(weight, scaleUnit, material.itemInventoryUnitSymbol), 4);
      logger.log('update material inventory on pause', material.code, quantity, material.itemInventoryUnitSymbol, '=', weight, scaleUnit);

      if (this.checkIndicatorValueError(currentWeight) === DeviceStatus.INDICATOR_ERROR) {
        logger.log('indicator error', scale.id, scale.description);
        return;
      }

      if (this.canReduceInventory && quantity > 0) {
        this.materialStore.updateMaterialInventoryUse(material, {
          quantity,
          comment: 'Bin paused',
          ticket: initialData.tId,
          isAutomatic: !initialData.materialId,
          binIds: [ bin.id ],
        });

        this.deviceStatus.set(bin.id, {
          ...this.getDeviceStatus(bin.id),
          weight: this.getBinWeightForStatus(b, currentWeight),
          weightUpdated: b.weightUpdated + weight,
        });
      }
    }, 500);
  },

  jogMode(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, mft: number): void {
    const { material, scale, jogSecondary, jogCounter } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const materialCode = this.materialStore.getMaterialName(material);
    const jogType = materialDetails.jogType || JogType.Primary;
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    const bin = jogType === JogType.Alternate && secondaryBin && jogSecondary ? secondaryBin : primaryBin;
    let message = '';
    const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const mData = runningData ? runningData.scales[scale.id].find((v: IWeighedDataToBatch) => v.material.itemId === material.itemId) : null;
    const { configuration } = this.settingsStore;
    const scaleWeight = this.getScaleWeight(scale);

    //check if material feed finish
    if (!runningData || (mData?.isFeedFinish)) {
      logger.warn(materialCode, 'feed already finish!!!');
      return;
    }

    logger.log('JOG mode', materialCode, mft, data.mft, scaleWeight, bin.description, jogSecondary);

    this.timers.clear(timerKey.device(scale.id));

    if (this.isAbort || !this.isActiveDrop(runningData)) {
      return;
    }

    if (primaryBin.aeratorId) {
      const aerator = this.devicesStore.getDeviceById(primaryBin.aeratorId);
      if (aerator?.turnOffDuringJogging) {
        aeratorSwitch(this.rootStore, primaryBin.aeratorId, false);
      }
    }

    const b = this.getDeviceStatus(bin.id);
    const binTarget = isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative ? mft : toNumber(data.mft);

    if (b.status === DeviceStatus.OVER && !b.overridden && !this.isFinishFeedDevice(primaryBin.id) && this.isModalVisible()) {
      this.timers.set(timerKey.device(scale.id), () => this.jogMode(data, runningData, mft), 1000, `Jog mode - scale #${scale.id}`);
      return;
    }

    if (this.isFinishFeedDevice(primaryBin.id)) {
      logger.log(material.code, 'feed finish over target:', b.weight, data.mft);
      this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== primaryBin.id);
      this.unHoldFeedDevice(primaryBin.id);

      // case when target is over and user could manipulate scale weight (open scale could change the actual)
      // that's why we read weight not from the scale anymore but from the bin status
      let weight = b.weight + b.prevWeight;
      if (this.settingsStore.configuration?.toleranceMode === ToleranceMode.NYDOTCumulative) {
        weight = b.status === DeviceStatus.OVER ? b.weight : this.getScaleWeight(scale);
      }
      this.updateInventory(data, weight, runningData, true);
      this.finishFeed(data, initialData, weight);

      return;
    }

    if (!this.isFinishFeedDevice(bin.id)) {
      if (this.isHoldFeed || initialData.isHoldLoad || this.isHoldFeedDevice(bin.id)) {
        const binStatus = this.getDeviceStatus(bin.id);
        if (!b.isManual && binStatus.status !== DeviceStatus.HOLD) {
          this.holdBin(bin);
        }
        this.timers.set(timerKey.device(scale.id), () => this.jogMode(data, runningData, mft), 2000, `Jog mode delay - scale #${scale.id}`);
        return;
      }
    }

    //pause the water scale feeding at 70% until the aggregates are complete
    if (
      FEATURE_LIVE_MOISTURE_FOR_WEIGHED
      && scaleWeight >= (mft * 0.7)
      && (
        this.materialStore.isWater(material.itemId)
        || (
          this.materialStore.isSecondWater(material.itemId)
          && toNumber(initialData.secondWater) === 100
        )
      )
      && waterShouldWaitForMoistureProbe(runningData, this)
    ) {
      logger.log('live moisture, water wait in jog mode at 70%', scaleWeight, mft, materialCode);

      this.deviceStatus.set(primaryBin.id, {
        ...this.getDeviceStatus(primaryBin.id),
        status: DeviceStatus.WAIT_MOIST,
      });

      if (secondaryBin) {
        this.deviceStatus.set(secondaryBin.id, {
          ...this.getDeviceStatus(secondaryBin.id),
          status: DeviceStatus.WAIT_MOIST,
        });
      }

      this.timers.clear(timerKey.device(scale.id));
      this.timers.set(timerKey.device(scale.id), () => this.jogMode(data, runningData, mft), 2000, `Jog mode delay - scale #${scale.id}`);

      return;
    }

    // keep data up to date when bin swap
    if (primaryBin.id !== data.bin.id) {
      data = {
        ...data,
        bin: primaryBin,
        material: {
          ...material,
        },
      };
      const index = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
      runningData.scales[scale.id][index] = data;

      if (secondaryBin && this.isHoldFeedDevice(secondaryBin.id)) {
        this.unHoldFeedDevice(secondaryBin.id);
      }

    }

    const materialBin = secondaryBin ? this.devicesStore.getBinFromMaterial(material, secondaryBin.id) : null;
    if (materialBin && !materialBin.isActive) {
      this.deviceStatus.set(secondaryBin?.id, this.getDeviceStatusDefault(secondaryBin));
    }

    const s = this.getDeviceStatus(scale.id);
    const toleranceTarget = isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative
      ? mft
      : b.target;
    let tolerance = this.getTolerance(materialDetails, toleranceTarget);
    logger.log('jog tolerance', material.code, tolerance, mft, b.target);

    if (configuration.onlyJogOnLastDrop
      && runningData.allDrops > 1 && runningData.currentDrop === runningData.allDrops && runningData.mixDesign) {
      const c = runningData.mixDesign.find((v: MixDesignItem) => v.constituentItemId === material.itemId);

      if (c?.quantityBatchSize) {
        tolerance = this.getTolerance(materialDetails, c.quantityBatchSize);
      }
    }

    if (b.status !== DeviceStatus.OVER && b.status !== DeviceStatus.PAUSE) {
      this.deviceStatus.set(scale.id, {
        ...s,
        status: DeviceStatus.SETTLING,
        error: false,
      });

      this.deviceStatus.set(bin.id, {
        ...b,
        target: binTarget,
        status: DeviceStatus.SETTLING,
        error: false,
      });
    }

    this.getScaleWeightAfterSettlingTime(scale, materialDetails.scaleSettlingTime, weight => {

      const b = this.getDeviceStatus(bin.id);

      this.deviceStatus.set(scale.id, {
        ...this.getDeviceStatus(scale.id),
        weight,
      });

      addFeedLog({
        batchTicketId: initialData.id,
        operationName: FeedOperation.SETTLING,
        duration: materialDetails.scaleSettlingTime,
        scaleWeight: this.getScaleWeight(scale),
        ...this.rootStore.weatherStore,
      }, data);

      logger.table(data.feedLogs);

      checkIfDischargeStartPercentForPumpsReady(this.rootStore, initialData);

      if (!this.isFinishFeedDevice(bin.id)) {

        if (weight < mft - tolerance?.under && materialDetails.jogLimit && jogCounter >= materialDetails.jogLimit) {
          logger.warn(`${materialCode}: Jog limit ${materialDetails.jogLimit} is reached!`, materialDetails.jogLimit, jogCounter);

          if (this.isOverride) {
            this.deviceStatus.set(bin.id, {
              ...b,
              target: binTarget,
              status: DeviceStatus.SETTLING,
              weight: this.getBinWeightForStatus(b, weight),
              error: false,
            });

            this.openJog({ ...data, jogCounter: 0 }, runningData, mft);

            return;
          } else {
            this.deviceStatus.set(bin.id, {
              ...b,
              target: binTarget,
              status: DeviceStatus.PAUSE,
              weight: this.getBinWeightForStatus(b, weight),
              error: true,
            });
            message = `${materialCode} Jog limit ${materialDetails.jogLimit} is reached! Press Override button [F2]`;
            this.showError(message);

            this.timers.set(timerKey.jog(scale.id), () => {
              this.jogMode(data, runningData, mft);
            }, 1000);

            return;
          }

        } else if (weight < mft - tolerance?.under) {
          this.deviceStatus.set(bin.id, {
            ...b,
            weight: this.getBinWeightForStatus(b, weight),
            target: binTarget,
            error: false,
          });

          return this.openJog(data, runningData, mft);

        } else if (weight > mft + tolerance?.over && !b.errorsOverridden.includes(DeviceStatus.OVER)) {

          if (data.overcapacityTarget && !data.isFeedFinishHeadWater) {
            this.finishFeed(data, initialData, weight);
            return;
          }

          logger.warn(`${materialCode}: Current weight is over target! ${weight} > ${mft} + ${tolerance?.over}, `, weight, mft, tolerance, this.isOverride);
          this.deviceStatus.set(bin.id, {
            ...b,
            target: binTarget,
            status: DeviceStatus.OVER,
            weight: this.getBinWeightForStatus(b, weight),
            error: true,
          });

          if (bin.id !== primaryBin.id) {
            this.deviceStatus.set(primaryBin.id, {
              ...b,
              target: binTarget,
              status: DeviceStatus.OVER,
              weight: this.getBinWeightForStatus(b, weight),
              error: true,
            });
          }

          const unit = scale.unitId ? this.getUnitById(scale.unitId) : materialDetails.itemBatchUnitSymbol;
          message = `${materialCode} current weight is over target! Press Override button [F2]`;
          this.showError(message);

          this.showOverTolerancePopup({
            material: materialCode,
            deviceId: bin.id,
            weight,
            target: mft,
            unit,
            uid: runningData.uid,
            currentDrop: runningData.currentDrop,
          });

          this.timers.set(timerKey.jog(scale.id), () => {
            this.jogMode(data, runningData, mft);
          }, 1000);
          return;

        }

        this.updateInventory(data, weight, runningData);

      } else {
        logger.log(materialCode, 'feed finish before target:', weight, data.mft);
        this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== bin.id);
        this.unHoldFeedDevice(bin.id);
        this.updateInventory(data, weight, runningData, true);
      }

      this.finishFeed(data, initialData, weight);
    });
  },

  finishFeed(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, weight: number) {
    const { configuration } = this.settingsStore;
    const { material, scale } = data;
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const jogType = materialDetails.jogType || JogType.Primary;
    const bins = this.getBins(data);
    const primaryBin = bins.primaryBin;
    const secondaryBin = bins.secondaryBin;

    const runningData = this.running.find(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
    const scaleIndex = runningData.scales[scale.id].findIndex((v: IWeighedDataToBatch) => v.material.itemId === material.itemId);
    const b = this.getDeviceStatus(primaryBin.id);
    const binTarget = isRunModeNewYork(runningData.runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative ? b.target : toNumber(data.mft);

    logger.log(`JOG mode FINISH ${material.code} -> next material for the same scale`);

    if (data.overcapacityTarget) {
      if (!data.isFeedFinishHeadWater) {
        runningData.scales[scale.id][scaleIndex].isFeedFinishHeadWater = true;
        const deviceStatus = {
          ...b,
          target: binTarget,
          weight: this.getBinWeightForStatus(b, weight),
          status: DeviceStatus.WAIT_FEED,
          error: false,
        };

        this.deviceStatus.set(primaryBin.id, deviceStatus);

        if (secondaryBin && jogType === JogType.Alternate) {
          this.deviceStatus.set(secondaryBin.id, deviceStatus);
        }

        logger.log('feed finish head water', material.code, data.mft);
        this.timers.set(timerKey.device(primaryBin.id), () => {
          this.batchDataForScale(scale.id, runningData);
        }, 1000, `Batch data for scale - bin #${primaryBin.id}`);

        return;

      } else if (!data.isFeedFinishWashWater) {
        runningData.scales[scale.id][scaleIndex].isFeedFinishWashWater = true;
        logger.log('feed finish wash water', material.code, data.mft);
      }

    }

    this.deviceStatus.set(primaryBin.id, {
      ...b,
      target: binTarget,
      weight: this.getBinWeightForStatus(b, weight),
      status: DeviceStatus.FINISH,
      error: false,
    });

    if (secondaryBin && jogType === JogType.Alternate) {
      this.deviceStatus.set(secondaryBin.id, {
        ...b,
        target: binTarget,
        weight: this.getBinWeightForStatus(b, weight),
        status: DeviceStatus.FINISH,
        error: false,
      });
    }

    if (primaryBin && !initialData.isWashout) {
      aeratorSwitch(this.rootStore, primaryBin.aeratorId, false);
      screwSwitch(this.rootStore, primaryBin.screwId, false);
      beltSwitch(this.rootStore, primaryBin.beltId, false);
      vibratorSwitch(this.rootStore, primaryBin.vibratorId, false);
    }

    this.setFeedFinish(data, runningData, scaleIndex);

    logger.log('finish feed', material.code, materialDetails.type);

    if (materialDetails.type === WeighedMaterialType.FineAggregate) {
      this.onBatchAction(initialData, BatchAction.FineAggComplete);
    } else if (materialDetails.type === WeighedMaterialType.Water || materialDetails.type === WeighedMaterialType.SecondWater) {
      this.onBatchAction(initialData, BatchAction.WaterScaleComplete);
    }

    if (!waterShouldWaitForMoistureProbe(runningData, this)) {
      this.restartHeadWaterPump(runningData);
    }

    this.timers.set(timerKey.device(primaryBin.id), () => {
      this.batchDataForScale(scale.id, runningData);
    }, 1000, `Batch data for scale - bin #${primaryBin.id}`);
  },

  checkWeightStatus(
    this: IBatchStore,
    scalePort: number,
    data: IWeighedDataToBatch,
    targetWeight: number,
    initialData: IBatchData,
    onFinish = () => {},
    onFirstBinFinish = () => {},
  ): void {

    if (this.weightsStatus[scalePort - 1] === '2') {
      onFirstBinFinish();
      onFinish();
      return;
    }

    const { material, scale } = data;

    reaction(
      () => this.statusCount,
      (counter, reaction) => {

        const bins = this.getBins(data);
        const primaryBin = bins.primaryBin || {};
        const binStatus = this.getDeviceStatus(primaryBin.id);

        if (
          this.isAbort
          || !this.isActiveDrop(initialData)
          || this.isHoldFeed
          || initialData.isHoldLoad
          || this.isHoldFeedDevice(primaryBin.id)
          || this.isHoldFeedDevice(material.scaleId)
          || binStatus.status === DeviceStatus.WAIT_MOIST
        ) {
          reaction.dispose();
        }

        if (targetWeight && scale.decumulative && this.getScaleWeight(scale) <= targetWeight) {
          logger.log(material.code, `no close port for scale: ${scale.description}, target weight reached:`, targetWeight);
          reaction.dispose();
          onFinish();
        }

        if (this.weightsStatus[scalePort - 1] === '1') {
          onFirstBinFinish();
        }

        if (this.weightsStatus[scalePort - 1] === '2') {
          reaction.dispose();
          onFinish();
        }

      },
    );
  },

  checkDecumScaleWeight(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData, onChange: (weight: number, reaction: any) => void = () => {}) {

    const { scale } = data;
    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        const currentWeight = this.getScaleWeight(scale);

        onChange(currentWeight, reaction);

        if (this.isAbort || !this.isActiveDrop(initialData)) {
          reaction.dispose();
        }

      });
  },

  checkAddingWeightToDecumScale(this: IBatchStore, data: IWeighedDataToBatch, initialData: IBatchData) {

    const { scale, material } = data;
    let weight = this.getScaleWeight(scale);

    reaction(
      () => this.channels,
      (weights, reaction) => {

        const scaleStatus = this.getDeviceStatus(scale.id);

        if (this.isAbort || !this.isActiveDrop(initialData) || scaleStatus.status === DeviceStatus.IDLE) {
          reaction.dispose();
          return;
        }

        const sm = this.getDeviceStatus(`${scale.id}-${material.code}`);
        const currentWeight = this.getScaleWeight(scale);

        if (this.canEmit) {
          this.deviceStatus.set(scale.id, {
            ...scaleStatus,
            weight: currentWeight,
          });
        }

        if (this.canEmit && currentWeight > weight &&
          ([DeviceStatus.HOLD, DeviceStatus.WAIT_DISCH].includes(sm.status) || this.isHoldFeedDevice(scale.id) || this.isHoldDischarge || initialData.isHoldLoad)) {
          logger.log('adding weight to decum scale:', scale.description, material.code, sm.status, currentWeight - weight);
          this.setTargetsForDecumScale(data, initialData, sm.status, true);
        }

        weight = currentWeight;

      });
  },

};
